/**
 * PATH: packages/email-hub/src/feature/lib/draftStore.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-DRAFTSTORE-001
 * PURPOSE: Canonical body of this email lib module. Lifted
 *          from apps/<tenant>/src/features/email/lib/draftStore.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/lib/draftStore.ts: thin re-export
 *     shim that points back here.
 */

// BUG-0029 + RL-01 — keys keep the build/brand namespace from `tlsKey()` and
// add the runtime organisation suffix (`.o<id>`). The unsuffixed v2/v3 keys
// are legacy quarantine sources; only drafts carrying explicit matching
// organisation provenance may migrate into an active org.
import { tlsKey } from '../../_internal/utils/tenantStorage'
import { getEmailTenantId } from '@quotech/tenant-shared/email/api'
import { emailPath } from './basePath'

export const DRAFT_STORAGE_KEY_V2 = tlsKey('email.compose.draft.v2')
export const DRAFT_STORAGE_KEY = tlsKey('email.compose.drafts.v3')

/** Drafts older than this are silently discarded on read. */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 3600 * 1000

export type DraftIntent = 'new' | 'reply' | 'forward' | 'reply_all'

export interface PersistedDraft {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body_html: string
  intent?: DraftIntent
  intent_email_id?: string | null
  /** For `new` drafts only: the per-window id that lets the operator
   *  keep SEVERAL independent "new message" drafts at once. Reply /
   *  forward drafts are keyed by their source email id instead, so
   *  this stays undefined for them. */
  draft_id?: string | null
  /** Send-as mailbox + outbound priority (BUG-2026-06-23): persisted in the
   *  instant cache so a reopen/resume before the server sync lands keeps the
   *  operator's chosen From mailbox + High/Low priority instead of reverting
   *  to defaults. Optional for back-compat with drafts written before this. */
  from_account_id?: number | null
  /** RL-01: runtime organisation that owned this local draft. Missing legacy
   *  drafts are never guessed into the currently selected organisation. */
  organisation_id?: number | null
  importance?: 'low' | 'normal' | 'high'
  saved_at: number
}

/**
 * `makeSlotKey(intent, intentEmailId, draftId?)` → unique slot identifier.
 *
 * - `reply` / `reply_all` / `forward` → `<intent>:<intentEmailId>` (one
 *   draft per thread — re-opening the same Reply restores it).
 * - `new` WITH a `draftId` → `new:<draftId>`, so the operator can have
 *   multiple independent "new message" drafts in flight (each Compose
 *   click mints a fresh id).
 * - `new` WITHOUT a `draftId` → legacy `new:new` (single slot;
 *   back-compat for any caller / stored draft that predates draft ids).
 */
export function makeSlotKey(
  intent: DraftIntent,
  intentEmailId: string | null,
  draftId?: string | null,
): string {
  if (intent === 'new') return draftId ? `new:${draftId}` : 'new:new'
  if (!intentEmailId) return 'new:new'
  return `${intent}:${intentEmailId}`
}

/** Parse a slot key back into its components. Mirror of makeSlotKey. */
export function parseSlotKey(
  key: string,
): { intent: DraftIntent; intentEmailId: string | null; draftId: string | null } {
  const idx = key.indexOf(':')
  if (idx < 0) return { intent: 'new', intentEmailId: null, draftId: null }
  const rawIntent = key.slice(0, idx)
  const id = key.slice(idx + 1)
  const intent: DraftIntent =
    rawIntent === 'reply' || rawIntent === 'forward' || rawIntent === 'reply_all'
      ? rawIntent
      : 'new'
  if (intent === 'new') {
    // `new:new` is the legacy single slot (no id); anything else is a
    // real per-window draft id.
    return { intent, intentEmailId: null, draftId: id && id !== 'new' ? id : null }
  }
  return { intent, intentEmailId: id, draftId: null }
}

export function draftsKeyForOrg(orgId: number | null): string {
  return orgId == null ? DRAFT_STORAGE_KEY : `${DRAFT_STORAGE_KEY}.o${orgId}`
}

function currentDraftsKey(): string {
  return draftsKeyForOrg(getEmailTenantId())
}

function readMapForKey(key: string): Record<string, PersistedDraft> {
  if (typeof window === 'undefined') return {}
  let raw: string | null
  try { raw = localStorage.getItem(key) } catch { return {} }
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, PersistedDraft>
    if (typeof parsed !== 'object' || parsed === null) return {}
    return parsed
  } catch {
    // Corrupt — drop the whole map so we don't keep failing.
    try { localStorage.removeItem(key) } catch { /* noop */ }
    return {}
  }
}

function readMap(): Record<string, PersistedDraft> {
  const orgId = getEmailTenantId()
  adoptDraftsForOrg(orgId)
  return readMapForKey(draftsKeyForOrg(orgId))
}

/**
 * Persist the map, returning whether the write actually landed. Emits a
 * draft-change ONLY on success so a failed (quota/disabled) write doesn't
 * tell listeners the store changed when it didn't. Callers that can shed
 * load (writeDraft) use the boolean to retry after eviction; callers that
 * only ever shrink the map (clear/list/migrate) ignore it via `writeMap`.
 */
function persistMapForKey(
  key: string,
  map: Record<string, PersistedDraft>,
): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(map))
    }
    emitDraftChange()
    return true
  } catch {
    // Storage full / disabled.
    return false
  }
}

function persistMap(map: Record<string, PersistedDraft>): boolean {
  return persistMapForKey(currentDraftsKey(), map)
}

function writeMap(map: Record<string, PersistedDraft>): void {
  persistMap(map)
}

/**
 * Move only drafts carrying explicit matching provenance from the old
 * brand-scoped v3 map into the active organisation map. Unknown legacy drafts
 * remain quarantined; guessing them into whichever org opens first would leak.
 */
export function adoptDraftsForOrg(orgId: number | null): void {
  if (typeof window === 'undefined' || orgId == null) return
  const scopedKey = draftsKeyForOrg(orgId)
  const legacy = readMapForKey(DRAFT_STORAGE_KEY)
  const scoped = readMapForKey(scopedKey)
  let changedLegacy = false
  let changedScoped = false
  for (const [slot, draft] of Object.entries(legacy)) {
    if (draft.organisation_id !== orgId) continue
    const existing = scoped[slot]
    if (!existing || (existing.saved_at || 0) < (draft.saved_at || 0)) {
      scoped[slot] = draft
      changedScoped = true
    }
    delete legacy[slot]
    changedLegacy = true
  }
  if (changedScoped) persistMapForKey(scopedKey, scoped)
  if (changedLegacy) persistMapForKey(DRAFT_STORAGE_KEY, legacy)
}

// ---------------------------------------------------------------------
// In-tab pub/sub. The native `storage` event only fires in OTHER tabs,
// so a draft autosaved from ComposePage wouldn't notify same-tab
// listeners (e.g. the EmailLayout Drafts badge, the DraftsPage row
// list). This tiny emitter closes that gap and lets any component
// drive its render off a `useSyncExternalStore`-style subscription
// without paying for a full Zustand store.
//
// Mark Newman 2026-05-17: "not auto saving properly into drafts as
// draft number didn't change". Root cause was the missing same-tab
// notification path — the writes were happening, the count was just
// stale on screen until the user navigated away and back.
// ---------------------------------------------------------------------
type DraftChangeListener = () => void
const draftChangeListeners = new Set<DraftChangeListener>()

function emitDraftChange(): void {
  for (const fn of draftChangeListeners) {
    try { fn() } catch { /* swallow - one bad listener can't break the rest */ }
  }
}

/**
 * Subscribe to draft-store mutations. Fires after every successful
 * write/clear in this tab AND on cross-tab `storage` events for the
 * draft key. Returns an unsubscribe handle.
 */
export function subscribeDraftChanges(listener: DraftChangeListener): () => void {
  draftChangeListeners.add(listener)
  // Forward cross-tab updates the first time anyone subscribes, so
  // newly opened windows see drafts saved in a sibling tab.
  if (draftChangeListeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('storage', onCrossTabStorage)
  }
  return () => {
    draftChangeListeners.delete(listener)
    if (draftChangeListeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', onCrossTabStorage)
    }
  }
}

function onCrossTabStorage(e: StorageEvent): void {
  if (
    e.key === currentDraftsKey()
    || e.key === DRAFT_STORAGE_KEY
    || e.key === DRAFT_STORAGE_KEY_V2
  ) {
    emitDraftChange()
  }
}

/**
 * Cheap snapshot of "how many drafts are there right now?" — used by
 * the Drafts nav badge. Same staleness window as listDrafts() so the
 * badge and the list agree on what's expired.
 */
export function countDrafts(now: number = Date.now()): number {
  const map = migrateLegacyV2(readMap())
  let count = 0
  for (const k of Object.keys(map)) {
    const d = map[k]
    if (d && now - (d.saved_at || 0) <= DRAFT_MAX_AGE_MS) count += 1
  }
  return count
}

/**
 * One-shot v2 → v3 migration. Idempotent. Called from every read +
 * write path so any tab can perform the migration on its next
 * interaction.
 */
function migrateLegacyV2(map: Record<string, PersistedDraft>): Record<string, PersistedDraft> {
  if (typeof window === 'undefined') return map
  // V2 has no organisation provenance. Never guess it into an active org.
  if (getEmailTenantId() != null) return map
  let legacyRaw: string | null
  try { legacyRaw = localStorage.getItem(DRAFT_STORAGE_KEY_V2) } catch { return map }
  if (!legacyRaw) return map
  let didMigrate = false
  try {
    const legacy = JSON.parse(legacyRaw) as PersistedDraft
    if (!legacy || typeof legacy.saved_at !== 'number') {
      try { localStorage.removeItem(DRAFT_STORAGE_KEY_V2) } catch { /* noop */ }
      return map
    }
    const slot = makeSlotKey(legacy.intent || 'new', legacy.intent_email_id ?? null)
    // Only migrate if the v3 slot is empty or older — never clobber
    // a newer v3 draft with a stale v2 leftover.
    const existing = map[slot]
    if (!existing || existing.saved_at < legacy.saved_at) {
      map[slot] = legacy
      didMigrate = true
    }
    try { localStorage.removeItem(DRAFT_STORAGE_KEY_V2) } catch { /* noop */ }
  } catch {
    try { localStorage.removeItem(DRAFT_STORAGE_KEY_V2) } catch { /* noop */ }
  }
  // Persist immediately so a follow-on read doesn't re-trigger the
  // migration path (after this point v2 is empty so it's idempotent
  // either way, but writing now means a single read sees the new
  // draft via the v3 path).
  if (didMigrate) writeMap(map)
  return map
}

/** Read a single draft by slot key. Returns null when missing or stale. */
export function readDraft(slotKey: string, now: number = Date.now()): PersistedDraft | null {
  // migrateLegacyV2 persists internally when it actually migrates, so
  // a plain read must NOT write. The previous missing-slot branch
  // re-wrote the whole map "in case migration added something", which
  // fired emitDraftChange on every read of an empty slot — every
  // OTHER draft consumer (dock badges, Drafts list) re-rendered each
  // time any compose surface probed for a draft that wasn't there.
  const map = migrateLegacyV2(readMap())
  const draft = map[slotKey]
  if (!draft) return null
  if (now - (draft.saved_at || 0) > DRAFT_MAX_AGE_MS) {
    delete map[slotKey]
    writeMap(map)
    return null
  }
  return draft
}

/** Write a draft to its slot.
 *
 * On a localStorage quota failure (a large pasted inline image can push the
 * map past the ~5 MB cap) we used to silently drop the write, so the draft the
 * operator was actively editing vanished on reopen with no warning. Now we
 * LRU-evict the OLDEST OTHER drafts and retry, so the in-progress draft always
 * wins the space race. If even the lone draft won't fit, the server-side draft
 * sync (client_ref) remains the durable fallback. */
export function writeDraft(slotKey: string, draft: PersistedDraft): void {
  const map = migrateLegacyV2(readMap())
  const organisationId = getEmailTenantId()
  map[slotKey] = {
    ...draft,
    organisation_id: organisationId ?? draft.organisation_id ?? null,
  }
  if (persistMap(map)) return
  // Quota hit — shed the oldest drafts (never the one we're saving) until it
  // fits or only the current draft remains.
  const evictionOrder = Object.keys(map)
    .filter((k) => k !== slotKey)
    .sort((a, b) => (map[a].saved_at || 0) - (map[b].saved_at || 0))
  for (const staleKey of evictionOrder) {
    delete map[staleKey]
    if (persistMap(map)) return
  }
  // Only the current draft is left and it STILL won't fit (e.g. a huge inline
  // image). Give up on the local cache silently — the server draft has it.
}

/** Clear a single slot. */
export function clearDraft(slotKey: string): void {
  const map = migrateLegacyV2(readMap())
  if (slotKey in map) {
    delete map[slotKey]
    writeMap(map)
  }
}

/** List every non-stale draft, freshest first. */
export function listDrafts(now: number = Date.now()): Array<{ slotKey: string; draft: PersistedDraft }> {
  const map = migrateLegacyV2(readMap())
  let changed = false
  for (const k of Object.keys(map)) {
    const d = map[k]
    if (!d || now - (d.saved_at || 0) > DRAFT_MAX_AGE_MS) {
      delete map[k]
      changed = true
    }
  }
  if (changed) writeMap(map)
  return Object.entries(map)
    .map(([slotKey, draft]) => ({ slotKey, draft }))
    .sort((a, b) => (b.draft.saved_at || 0) - (a.draft.saved_at || 0))
}

/**
 * `resumeHref(draft)` → the URL that ComposePage opens in to
 * re-hydrate this draft in full-screen mode.
 *
 * Used by DraftsPage's "Expand to full screen" fallback. The
 * primary resume path now opens the floating dock instead — see
 * ComposeDock + composeDockStore.
 */
export function resumeHref(draft: PersistedDraft): string {
  const intent = draft.intent || 'new'
  const id = draft.intent_email_id
  // emailPath() so the resume URL respects the host mount point
  // (/emails on master, /email on SAM, / on the standalone hub).
  const base = emailPath('/compose')
  if (intent === 'reply' && id) return `${base}?reply=${encodeURIComponent(id)}`
  if (intent === 'reply_all' && id) return `${base}?reply_all=${encodeURIComponent(id)}`
  if (intent === 'forward' && id) return `${base}?forward=${encodeURIComponent(id)}`
  // A `new` draft carries its own per-window id so resuming reopens the
  // SAME slot (not a fresh blank compose) — multiple new drafts.
  if (draft.draft_id) return `${base}?draft=${encodeURIComponent(draft.draft_id)}`
  return base
}

/** Tidy preview line for a draft card. */
export function previewLine(draft: PersistedDraft, maxChars: number = 140): string {
  const stripped = (draft.body_html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:])/g, '$1')
    .trim()
  if (!stripped) return '(no content)'
  return stripped.length > maxChars ? `${stripped.slice(0, maxChars).trim()}…` : stripped
}
